import { Box } from '@lego/klik-ui';
import { type ChangeEvent, type DragEvent, useState } from 'react';
import { FileLabelModal } from '@/components/file/upload/FileLabelModal';
import {
  FILE_READ_ERROR,
  getUploadErrorMessage,
  validateFileName,
} from '@/components/file/upload/uploadValidation';
import { useFileUpload } from '@/hooks/common/useFileUpload';
import { UploadDropZone } from './UploadDropZone';
import { UploadProgressBar } from './UploadProgressBar';

type BaseUploadData = {
  fileName: string;
  fileContents: string;
  label?: string;
};

type Props = {
  onUpload: (request: BaseUploadData) => Promise<unknown>;
  fileType?: 'device' | 'workspace';
};

export const UploadFile = ({ onUpload, fileType }: Props) => {
  const [fileContents, setFileContents] = useState('');
  const upload = useFileUpload({ resetOnSuccess: true });

  const processFile = (selectedFile: File) => {
    const validationError = validateFileName(selectedFile.name, fileType ?? 'device');
    if (validationError) {
      upload.setValidationError(selectedFile, validationError);
      return;
    }

    upload.setFile(selectedFile);

    const reader = new FileReader();
    reader.onerror = () => upload.failUpload(FILE_READ_ERROR);
    reader.onload = () => {
      const base64Content = (reader.result as string).split(',')[1];
      setFileContents(base64Content);

      if (fileType === 'workspace') {
        upload.setUploadStatus('preview');
      } else {
        void performUploadFile(selectedFile.name, base64Content);
      }
    };
    reader.readAsDataURL(selectedFile);
  };

  const performUploadFile = async (fileName: string, contents: string, fileLabel?: string) => {
    upload.startUpload();
    try {
      await onUpload({
        fileName,
        fileContents: contents,
        ...(fileLabel && { label: fileLabel }),
      });
      upload.finishUpload();
    } catch (err) {
      upload.failUpload(getUploadErrorMessage(err));
    }
  };

  const uploadFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    processFile(files[0]);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    upload.setIsInDropArea(false);
    uploadFiles(e.dataTransfer.files);
  };
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFiles(e.target.files);
  };

  return (
    <>
      <Box
        alignItems="center"
        backgroundColor="white"
        borderRadius="md"
        boxShadow={2}
        display="flex"
        justifyContent="center"
        minHeight="150px"
        mt={4}
        p={4}
        textAlign="center"
      >
        {upload.uploadStatus === 'idle' && (
          <UploadDropZone
            isInDropArea={upload.isInDropArea}
            onDragEnter={upload.handleDragEnter}
            onDragLeave={upload.handleDragLeave}
            onDragOver={upload.handleDragOver}
            onDrop={handleDrop}
            onFileSelect={handleFileSelect}
          />
        )}

        {(upload.uploadStatus === 'uploading' ||
          upload.uploadStatus === 'success' ||
          upload.uploadStatus === 'error') && (
          <UploadProgressBar
            errorMessage={upload.errorMessage}
            fileName={upload.file?.name}
            onGoBack={upload.uploadStatus === 'error' ? upload.reset : undefined}
            progress={upload.progress}
            status={upload.uploadStatus}
          />
        )}
      </Box>

      <FileLabelModal
        initialLabel={upload.file?.name || ''}
        isOpen={upload.uploadStatus === 'preview'}
        onClose={upload.reset}
        onSubmit={(label) => {
          if (upload.file && fileContents) {
            void performUploadFile(upload.file.name, fileContents, label);
          }
        }}
      />
    </>
  );
};
